'use client';
import { Container } from '@geberit/gdds';

// styles
import styles from './content.module.scss';

// types
import type {
  ContentElementPayload,
  ShowroomLocatorTeaserPayload,
} from 'components/ContentElements/content-elements-payload';

// components
import HeroImageC from 'components/ContentElementsGdds/hero-image/hero-image-c';
import { HeroImageGdds } from 'components/ContentElementsGdds/hero-image/hero-image-gdds';
import { ContentArea } from 'components/ContentArea/ContentArea';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { IntroText } from 'components/ContentElementsGdds/IntroText/intro-text';
import { AnchorBar } from 'components/ContentElementsGdds/anchorbar/anchorbar';
import { RelatedLinks } from 'components/RelatedLinks/nordics/related-links';
import SocialShare from 'components/SocialShare/SocialShare';
import Layout from './layout';

// utils
import { isEmpty } from 'utils/is-empty';
import { useContent } from 'components/Page/page-hooks';
import { useShowSocialShare } from 'utils/hooks/use-show-social-share';
import { buildSize, gridSizes } from 'utils/gridSize';

type ContentProps = {
  hero: ISlider;
  slider: ISlider;
  collapseAnchor?: boolean;
  pageAnchorTitle?: string;
};

export function Content() {
  const page = useContent<{}, ContentProps>();
  const showSocialShare = useShowSocialShare();

  if (isEmpty(page) || isEmpty(page.contentAreas)) {
    return null;
  }

  const {
    breadcrumbs,
    metaData,
    page: {
      slider,
      intro,
      text,
      headline,
      subHeadline,
      hero,
      showAnchor,
      collapseAnchor,
      pageAnchorTitle,
    },
    contentAreas: { content },
    relatedLinks,
    ...props
  } = page;
  const heroTypeC = !isEmpty(hero?.items) && hero.items[0].type === 'hero_c_item';

  return (
    <Layout {...props} metaData={metaData}>
      {!isEmpty(hero?.items) && !heroTypeC && <HeroImageGdds {...hero} isSeoConformCarousel />}
      <Breadcrumb items={breadcrumbs} className={styles.breadcrumbs} />
      <HeroImageGdds {...slider} />
      {heroTypeC && <HeroImageC {...hero.items?.[0]} />}
      {(headline || subHeadline) && (
        <IntroText
          className={styles.intro}
          title={headline}
          subtitle={subHeadline}
          text={text}
          intro={intro}
          border="disable-border"
          disableInEdit
          // -> InEdit not working on page level because of #327791
          // can possibly be activated later again
          // titlePreviewId="#pt_contentHeadline"
          // subtitlePreviewId="#pt_contentSubHeadline"
        />
      )}
      {showAnchor && (
        <AnchorBar
          content={content as Exclude<ContentElementPayload, ShowroomLocatorTeaserPayload>[]}
          collapseAnchor={collapseAnchor}
          pageAnchorTitle={pageAnchorTitle}
        />
      )}
      <ContentArea content={content} contentType="content" {...props} />

      {showSocialShare && (
        <Container maxContentWidth={buildSize(gridSizes.gddsFullGrid)}>
          <SocialShare contentType="page" metaData={metaData} useInContentPage />
        </Container>
      )}

      <RelatedLinks
        title={relatedLinks?.title}
        anchor={relatedLinks?.anchor}
        categories={relatedLinks?.categories}
      />
    </Layout>
  );
}
